import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { EditorChangeType } from '../types';
import { AbstractCommand } from './AbstractCommand';

import type { DubOptions } from '../models/Timeline';
import type { VideoEditor } from '../VideoEditor';

/**
 * Sets the values a clip stores about its dub: the language, which voice is
 * heard, whether the recording's own music plays under it, the dubbed voice's
 * level (PLAT-11899) and, on Both, the original recording's (PLAT-11927).
 *
 * **It creates no audio and no tracks.** That is the change PLAT-11511 makes.
 * The dubbed audio is worked out by the dub engine every time the timeline is
 * turned into a document, from these three values and from the description held
 * in memory inside the editor, so it can never drift out of step with a picture
 * that has been trimmed or cut. This command's whole job is to write down what
 * the person chose.
 *
 * **It writes no undo entry, takes no state copy for the user's undo list and
 * asks for no save.** Both hosting screens do those for it: they replace the
 * editor's `executeCommand` method, copy the whole clip onto the user-visible
 * undo list before running the command and schedule the save afterwards. Adding
 * any of that here would put two copies on that list for one action, so the
 * second Undo press would restore an identical clip and look broken.
 *
 * **It reverses itself by putting the previous values back**, which is the
 * whole of the reversal, because those values are the whole of what it changed.
 */
export class SetDubOptionsCommand extends AbstractCommand {
  private readonly options: DubOptions;

  /** What the clip held before this ran. `null` means it had never been dubbed. */
  private previousOptions: DubOptions | null = null;

  private executed = false;

  constructor(editor: VideoEditor, options: DubOptions) {
    super(editor);
    this.options = { ...options };
  }

  getName(): string {
    return 'SetDubOptionsCommand';
  }

  getDescription(): string {
    const { language, voiceMode } = this.options;
    if (voiceMode === 'original' || !language) return 'Use the original voice';
    return voiceMode === 'both'
      ? `Play the ${language} dub over the recording`
      : `Play the ${language} dub`;
  }

  execute(): boolean {
    // Deliberately no `super.execute()`: that captures a timeline snapshot this
    // command has no use for, because it changes no element.
    this.previousOptions = this.editor.timeline.dubOptions;
    this.editor.timeline.setDubOptions(this.options);
    this.executed = true;

    VideoEditorLogger.logCommand(this, 'start', {
      language: this.options.language,
      voiceMode: this.options.voiceMode,
      musicMode: this.options.musicMode,
      voiceVolume: this.options.voiceVolume,
      originalVolume: this.options.originalVolume,
    });

    // PLAT-11905 — the captions follow the voice in the same pass: the
    // dub-timed set exactly when the dubbed voice is heard.
    this.editor.syncCaptionSetToVoice();
    this.editor.markDirty('set_dub_options_command');
    // What asks for a new document, which is where the dubbed audio is built.
    this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED);
    return true;
  }

  undo(): boolean {
    if (!this.executed) return false;

    this.editor.timeline.setDubOptions(this.previousOptions);
    this.executed = false;

    this.editor.syncCaptionSetToVoice();
    this.editor.markDirty('set_dub_options_command_undo');
    this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED);
    return true;
  }
}
